import { logger } from '@fe/core';
import { Subscription } from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { MapAutoScreenshotTarget } from '../../types/settings';
import { EventBus } from '../contexts/event-bus';
import { useFocusedWorkspaceItemContext } from '../contexts/workspace/workspace-item-context';
import { useFocusedWorkspaceSelector } from '../features/home/hooks/use-view';
import { ScreenshotCommandType } from '../features/home/layers/MapScreenshot';
import { setSlideshow } from '../state/slices/global-slice';
import { workspaceSwitchView } from '../state/slices/workspace-slice';
import { useAppDispatch, useAppSelector, useAppStore } from './store-hooks';
import useMap from './use-map';

export function useSlideShow() {
    const map = useMap();
    const workspaceId = useFocusedWorkspaceItemContext().id;
    const selectedViewId = useFocusedWorkspaceSelector((x) => x.view.id);
    const active = useAppSelector((x) => x.global.slideshow);
    const period = useAppSelector((x) => x.settings.timeToShowEachViewSeconds);
    const hasAutoScreenshotRight = useAppSelector((x) => x.user.rights.webSnapshot);
    const autoScreenshot = useAppSelector((x) => x.settings.mapImageAutoSlideShow);
    const autoScreenshotTarget = useAppSelector((x) => x.settings.mapImageAutoSlideShowTarget);
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const mapIdleSubscription = useRef<Subscription | undefined>(undefined);

    useEffect(() => {
        if (!active) {
            return;
        }

        const timeout = self.setTimeout(() => {
            mapIdleSubscription.current?.unsubscribe();
            mapIdleSubscription.current = undefined;
            const state = store.getState();
            const views = state.views.items.filter((x) => x.includeInSlideshow);
            let index = views.findIndex((x) => x.id === selectedViewId);
            if (index === -1) {
                index = 0;
            } else {
                index++;
                if (index >= views.length) {
                    index = 0;
                }
            }

            const newView = views[index];
            if (!newView) {
                logger.warn('Cannot find next view in slideshow');
                dispatch(setSlideshow({ active: false }));
                return;
            }

            dispatch(workspaceSwitchView({ workspaceId: workspaceId, view: newView }))
                .unwrap()
                .then(() => {
                    if (hasAutoScreenshotRight && autoScreenshot) {
                        mapIdleSubscription.current = map?.on('idle', () => {
                            mapIdleSubscription.current?.unsubscribe();
                            mapIdleSubscription.current = undefined;
                            self.setTimeout(() => {
                                EventBus.fire('take-map-screenshot', {
                                    workspaceIndex: workspaceId,
                                    type: ScreenshotCommandType.slideShowAutomatic,
                                    upload: (autoScreenshotTarget & MapAutoScreenshotTarget.upload) !== 0,
                                    saveToFile: (autoScreenshotTarget & MapAutoScreenshotTarget.file) !== 0,
                                    fileName: newView.name
                                });
                            }, 250);
                        });
                    }
                });
        }, period * 1000);

        return () => {
            self.clearTimeout(timeout);
            mapIdleSubscription.current?.unsubscribe();
            mapIdleSubscription.current = undefined;
        };
    }, [map, workspaceId, selectedViewId, active, period, autoScreenshot, hasAutoScreenshotRight, autoScreenshotTarget]);

    return active;
}
